import {
  matchPath,
  resolvePath,
  useLocation,
  useNavigate,
  useResolvedPath,
} from "react-router-dom";

import { ComboboxItemGroup, Select } from "@mantine/core";

import classes from "./SettingsSectionSelect.module.css";
import { SettingsNavLink } from "./types";

/**
 * The settings sections as a select, for phones, where the sidebar doesn't fit. It lists the same
 * sections as the sidebar, grouped the same way, and shows the one the page is on.
 */
export default function SettingsSectionSelect({ links }: { links: SettingsNavLink[] }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const settingsPath = useResolvedPath(".").pathname;

  // The sidebar's NavLink rule: a link is active on its own path and, unless `end`, on the
  // pages under it, such as a form inside Forms Library.
  const activeLink = links.find(({ to, end }) =>
    matchPath({ path: resolvePath(to, settingsPath).pathname, end }, pathname),
  );

  return (
    <Select
      mb="lg"
      hiddenFrom="sm"
      allowDeselect={false}
      data={toSelectData(links)}
      aria-label="Settings section"
      placeholder="Choose a section"
      value={activeLink?.to ?? null}
      data-testid="settings-section-select"
      onChange={(to) => to && navigate(to)}
      classNames={{ input: classes.input, option: classes.option }}
    />
  );
}

/**
 * Groups the links under their section headings, keeping their order.
 *
 * @param links - The settings links the user sees, in sidebar order.
 * @returns Select data with one group per section.
 */
function toSelectData(links: SettingsNavLink[]): ComboboxItemGroup[] {
  return links.reduce<ComboboxItemGroup[]>((groups, { to, label, isDisabled, section }) => {
    const item = { value: to, label, disabled: isDisabled };
    const lastGroup = groups.at(-1);
    if (lastGroup?.group === section) lastGroup.items.push(item);
    else groups.push({ group: section, items: [item] });
    return groups;
  }, []);
}
